import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPencil,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const SQFT_TO_SQM = 0.092903;

const normalizeUnitOfMeasure = (value) => {
  const normalized = String(value || "sqm").trim().toLowerCase();
  if (["m²", "m2", "sqm"].includes(normalized)) {
    return "sqm";
  }
  if (["ft²", "ft2", "sqft"].includes(normalized)) {
    return "sqft";
  }
  return normalized;
};

const parseNumberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundToTwo = (value) => Number(parseNumberOrZero(value).toFixed(2));

const formatRateValue = (value) =>
  parseNumberOrZero(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const buildEmptyMixRow = (methodCodes = []) =>
  Object.fromEntries(methodCodes.map((methodCode) => [methodCode, 0]));

const buildEmptyMethodCosts = (methodCodes = []) =>
  Object.fromEntries(methodCodes.map((methodCode) => [methodCode, 0]));

const buildEmptyCombinationMix = (combinationCodes = [], methodCodes = []) =>
  Object.fromEntries(
    combinationCodes.map((combinationCode) => [
      combinationCode,
      buildEmptyMixRow(methodCodes),
    ]),
  );

const normalizeCombinationMix = (
  rawMix,
  combinationCodes = [],
  methodCodes = [],
) => {
  const source =
    rawMix && typeof rawMix === "object" && !Array.isArray(rawMix) ? rawMix : {};

  return Object.fromEntries(
    combinationCodes.map((combinationCode) => {
      const sourceRow =
        source[combinationCode] &&
        typeof source[combinationCode] === "object" &&
        !Array.isArray(source[combinationCode])
          ? source[combinationCode]
          : {};

      return [
        combinationCode,
        Object.fromEntries(
          methodCodes.map((methodCode) => [
            methodCode,
            parseNumberOrZero(sourceRow[methodCode]),
          ]),
        ),
      ];
    }),
  );
};

const normalizeMethodCosts = (rawCosts, methodCodes = []) => {
  const source =
    rawCosts && typeof rawCosts === "object" && !Array.isArray(rawCosts)
      ? rawCosts
      : {};

  return Object.fromEntries(
    methodCodes.map((methodCode) => [
      methodCode,
      parseNumberOrZero(source[methodCode]),
    ]),
  );
};

const resolveDefaultCurrency = (settings) =>
  String(
    settings?.currency_units?.default_currency?.value ||
      settings?.currency_units?.currency?.value ||
      settings?.currency_units?.currency?.setting_value ||
      "CAD",
  )
    .trim()
    .toUpperCase();

const resolveDefaultUOM = (settings) =>
  normalizeUnitOfMeasure(
    settings?.currency_units?.preferred_unit?.value ||
      settings?.currency_units?.preferred_units?.value ||
      settings?.currency_units?.preferred_unit?.setting_value ||
      settings?.currency_units?.preferred_units?.setting_value ||
      "m²",
  );

const SurfacePrepMethodsManagement = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState("methods"); // 'methods' or 'blended'

  // Methods tab state
  const [methods, setMethods] = useState([]); // Now grouped by year: [{year: 2024, methods: [...], currency: 'CAD', unitOfMeasure: 'sqm'}, ...]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [methodModalError, setMethodModalError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [editingYear, setEditingYear] = useState(null);

  // Blended costs tab state
  const [blendedCosts, setBlendedCosts] = useState([]);
  const [blendedLoading, setBlendedLoading] = useState(false);
  const [blendedError, setBlendedError] = useState("");
  const [showBlendedModal, setShowBlendedModal] = useState(false);
  const [showBlendedCopyModal, setShowBlendedCopyModal] = useState(false);
  const [blendedCopySourceYear, setBlendedCopySourceYear] = useState(null);
  const [blendedCopyTargetYear, setBlendedCopyTargetYear] = useState("");
  const [blendedCopyError, setBlendedCopyError] = useState("");
  const [blendedCopying, setBlendedCopying] = useState(false);
  const [combinations, setCombinations] = useState([]);
  const [surfacePrepMethods, setSurfacePrepMethods] = useState([]);
  const [blendedFilters, setBlendedFilters] = useState({
    year: "",
    combination_code: "",
    is_active: "",
  });
  const [blendedPagination, setBlendedPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [formData, setFormData] = useState({
    method_code: "",
    method_name: "",
    sort_order: 99,
  });
  const [defaultSystemSettings, setDefaultSystemSettings] = useState({
    currency: "CAD",
    uom: "sqm",
  });

  // Blended cost form data (matrix keyed by combination code -> method code)
  const [blendedFormData, setBlendedFormData] = useState({
    year: new Date().getFullYear(),
    methodCosts: {},
    combinationMixPercentages: {},
    currency: "CAD",
    unitOfMeasure: "sqm",
    isActive: true,
  });
  const [normalizedCadPerSqmMethodCosts, setNormalizedCadPerSqmMethodCosts] = useState({});
  const [methodCostsLoading, setMethodCostsLoading] = useState(false);
  const conversionRateCacheRef = useRef({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const sortMethodsBySortOrder = (items = []) =>
    [...items].sort((a, b) => {
      const aSort = Number(a.sortOrder ?? a.sort_order ?? 99);
      const bSort = Number(b.sortOrder ?? b.sort_order ?? 99);
      if (aSort !== bSort) return aSort - bSort;

      const aCode = String(a.methodCode || a.method_code || "");
      const bCode = String(b.methodCode || b.method_code || "");
      return aCode.localeCompare(bCode);
    });

  // Derived: combination codes for table and form (dynamic from API)
  const combinationCodes = useMemo(
    () => combinations.map((c) => c.code).filter(Boolean),
    [combinations],
  );
  const sortedSurfacePrepMethods = useMemo(
    () => sortMethodsBySortOrder(surfacePrepMethods),
    [surfacePrepMethods],
  );
  // Derived: SP method codes for blend grid columns (dynamic from surface prep methods)
  const spMethodCodes = useMemo(
    () =>
      sortedSurfacePrepMethods
        .map((method) => method.methodCode || method.method_code)
        .filter(Boolean),
    [sortedSurfacePrepMethods],
  );

  const getMethodIsActive = (method) =>
    method.isActive !== undefined
      ? method.isActive
      : method.is_active !== false;

  const getConversionRateForYear = useCallback(async (year, fromCurrency, toCurrency) => {
    const normalizedFrom = String(fromCurrency || "CAD").trim().toUpperCase();
    const normalizedTo = String(toCurrency || "CAD").trim().toUpperCase();

    if (!normalizedFrom || !normalizedTo || normalizedFrom === normalizedTo) {
      return 1;
    }

    const cacheKey = `${year}|${normalizedFrom}|${normalizedTo}`;
    if (conversionRateCacheRef.current[cacheKey] !== undefined) {
      return conversionRateCacheRef.current[cacheKey];
    }

    const response = await api.get(
      `/currency-conversion-rates/year/${year}/${normalizedFrom}/${normalizedTo}`,
    );
    const rate = parseNumberOrZero(response.data?.data?.conversionRate) || 1;
    conversionRateCacheRef.current[cacheKey] = rate;
    return rate;
  }, []);

  const convertRateBetweenSystems = useCallback(
    async ({ amount, fromCurrency, toCurrency, fromUnit, toUnit, year }) => {
      const normalizedFromUnit = normalizeUnitOfMeasure(fromUnit);
      const normalizedToUnit = normalizeUnitOfMeasure(toUnit);
      const currencyRate = await getConversionRateForYear(
        year,
        fromCurrency,
        toCurrency,
      );

      let converted = parseNumberOrZero(amount) * currencyRate;

      if (normalizedFromUnit === "sqft" && normalizedToUnit === "sqm") {
        converted /= SQFT_TO_SQM;
      } else if (normalizedFromUnit === "sqm" && normalizedToUnit === "sqft") {
        converted *= SQFT_TO_SQM;
      }

      return roundToTwo(converted);
    },
    [getConversionRateForYear],
  );

  const convertMethodCostsMap = useCallback(
    async ({
      methodCosts,
      fromCurrency,
      toCurrency,
      fromUnit,
      toUnit,
      year,
    }) => {
      const normalizedMethodCosts = normalizeMethodCosts(methodCosts, spMethodCodes);

      const entries = await Promise.all(
        spMethodCodes.map(async (methodCode) => [
          methodCode,
          await convertRateBetweenSystems({
            amount: normalizedMethodCosts[methodCode],
            fromCurrency,
            toCurrency,
            fromUnit,
            toUnit,
            year,
          }),
        ]),
      );

      return Object.fromEntries(entries);
    },
    [convertRateBetweenSystems, spMethodCodes],
  );

  // Fetch default system settings and conversion rate
  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        // Get default currency and UOM from settings
        const settingsResponse = await api.get("/settings/categories");
        if (settingsResponse.data.success) {
          const settings = settingsResponse.data.data;
          const currency = resolveDefaultCurrency(settings);
          const uom = resolveDefaultUOM(settings);
          setDefaultSystemSettings({ currency, uom });
        }
      } catch (err) {
        console.error("Error fetching default settings:", err);
      }
    };
    fetchDefaultSettings();
  }, []);

  // Fetch methods
  const fetchMethods = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await api.get(`/surface-prep-methods?${params}`);
      if (response.data.success) {
        setMethods(sortMethodsBySortOrder(response.data.data || []));
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError("Failed to fetch surface preparation methods");
      console.error("Error fetching methods:", err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Fetch surface prep methods for cost calculation
  const fetchSurfacePrepMethodsForCalculation = useCallback(async () => {
    try {
      const response = await api.get("/surface-prep-methods?limit=100");
      if (response.data.success) {
        setSurfacePrepMethods(sortMethodsBySortOrder(response.data.data || []));
      }
    } catch (err) {
      console.error(
        "Error fetching surface prep methods for calculation:",
        err,
      );
    }
  }, []);

  // Fetch combinations for blended costs
  const fetchCombinations = useCallback(async () => {
    try {
      const response = await api.get("/blended-surface-prep/combinations");
      if (response.data.success) {
        setCombinations(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching combinations:", err);
    }
  }, []);

  // Fetch blended costs (grouped by year)
  const fetchBlendedCosts = useCallback(async () => {
    setBlendedLoading(true);
    setBlendedError("");
    try {
      const params = new URLSearchParams({
        page: blendedPagination.currentPage.toString(),
        limit: "20",
      });

      if (blendedFilters.year) params.append("year", blendedFilters.year);
      if (blendedFilters.combination_code)
        params.append("combination_code", blendedFilters.combination_code);
      if (blendedFilters.is_active !== "")
        params.append("is_active", blendedFilters.is_active);

      const response = await api.get(`/blended-surface-prep?${params}`);

      if (response.data.success) {
        setBlendedCosts(response.data.data);
        setBlendedPagination(response.data.pagination);
      }
    } catch (err) {
      setBlendedError(
        err.response?.data?.message || "Failed to fetch blended costs",
      );
    } finally {
      setBlendedLoading(false);
    }
  }, [
    blendedPagination.currentPage,
    blendedFilters.year,
    blendedFilters.combination_code,
    blendedFilters.is_active,
  ]);

  useEffect(() => {
    if (activeTab === "methods") {
      fetchMethods();
    } else if (activeTab === "blended") {
      fetchBlendedCosts();
      fetchCombinations();
      fetchSurfacePrepMethodsForCalculation();
    }
  }, [
    activeTab,
    fetchMethods,
    fetchBlendedCosts,
    fetchCombinations,
    fetchSurfacePrepMethodsForCalculation,
  ]);

  useEffect(() => {
    if (!showBlendedModal) {
      return;
    }

    const normalizedMix = normalizeCombinationMix(
      blendedFormData.combinationMixPercentages,
      combinationCodes,
      spMethodCodes,
    );
    const currentSignature = JSON.stringify(
      blendedFormData.combinationMixPercentages || {},
    );
    const normalizedSignature = JSON.stringify(normalizedMix);

    if (currentSignature !== normalizedSignature) {
      setBlendedFormData((prev) => ({
        ...prev,
        combinationMixPercentages: normalizedMix,
      }));
    }
  }, [
    showBlendedModal,
    blendedFormData.combinationMixPercentages,
    combinationCodes,
    spMethodCodes,
  ]);

  useEffect(() => {
    if (!showBlendedModal) {
      return;
    }

    const normalizedMethodCosts = normalizeMethodCosts(
      blendedFormData.methodCosts,
      spMethodCodes,
    );
    const currentSignature = JSON.stringify(blendedFormData.methodCosts || {});
    const normalizedSignature = JSON.stringify(normalizedMethodCosts);

    if (currentSignature !== normalizedSignature) {
      setBlendedFormData((prev) => ({
        ...prev,
        methodCosts: normalizedMethodCosts,
      }));
    }
  }, [
    showBlendedModal,
    blendedFormData.methodCosts,
    spMethodCodes,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadNormalizedCadPerSqmMethodCosts = async () => {
      if (!showBlendedModal || spMethodCodes.length === 0) {
        setNormalizedCadPerSqmMethodCosts({});
        return;
      }

      try {
        const normalizedMethodCosts = await convertMethodCostsMap({
          methodCosts: blendedFormData.methodCosts,
          fromCurrency: blendedFormData.currency,
          toCurrency: "CAD",
          fromUnit: blendedFormData.unitOfMeasure,
          toUnit: "sqm",
          year: blendedFormData.year,
        });

        if (!cancelled) {
          setNormalizedCadPerSqmMethodCosts(normalizedMethodCosts);
        }
      } catch (err) {
        console.error("Error converting canonical blended method costs:", err);
        if (!cancelled) {
          setNormalizedCadPerSqmMethodCosts({});
        }
      }
    };

    loadNormalizedCadPerSqmMethodCosts();

    return () => {
      cancelled = true;
    };
  }, [
    showBlendedModal,
    blendedFormData.methodCosts,
    blendedFormData.currency,
    blendedFormData.unitOfMeasure,
    blendedFormData.year,
    convertMethodCostsMap,
    spMethodCodes,
  ]);

  const blendedRowMetrics = useMemo(
    () =>
      Object.fromEntries(
        combinationCodes.map((combinationCode) => {
          const row =
            blendedFormData.combinationMixPercentages?.[combinationCode] || {};
          const percentageTotal = roundToTwo(
            spMethodCodes.reduce(
              (sum, methodCode) => sum + parseNumberOrZero(row[methodCode]),
              0,
            ),
          );
          const totalCost = roundToTwo(
            spMethodCodes.reduce(
              (sum, methodCode) =>
                sum +
                parseNumberOrZero(blendedFormData.methodCosts?.[methodCode]) *
                  (parseNumberOrZero(row[methodCode]) / 100),
              0,
            ),
          );
          const normalizedCadPerSqmTotalCost = roundToTwo(
            spMethodCodes.reduce(
              (sum, methodCode) =>
                sum +
                parseNumberOrZero(normalizedCadPerSqmMethodCosts?.[methodCode]) *
                  (parseNumberOrZero(row[methodCode]) / 100),
              0,
            ),
          );

          return [
            combinationCode,
            {
              percentageTotal,
              totalCost,
              normalizedCadPerSqmTotalCost,
              isValid: Math.abs(percentageTotal - 100) <= 0.01,
            },
          ];
        }),
      ),
    [
      combinationCodes,
      spMethodCodes,
      blendedFormData.methodCosts,
      blendedFormData.combinationMixPercentages,
      normalizedCadPerSqmMethodCosts,
    ],
  );

  const invalidCombinationCodes = useMemo(
    () =>
      combinationCodes.filter(
        (combinationCode) => !blendedRowMetrics[combinationCode]?.isValid,
      ),
    [combinationCodes, blendedRowMetrics],
  );

  const canSaveBlendedForm =
    combinationCodes.length > 0 &&
    spMethodCodes.length > 0 &&
    !methodCostsLoading &&
    invalidCombinationCodes.length === 0;

  const handleBlendedPercentageChange = useCallback(
    (combinationCode, methodCode, rawValue) => {
      setBlendedFormData((prev) => {
        const normalizedMix = normalizeCombinationMix(
          prev.combinationMixPercentages,
          combinationCodes,
          spMethodCodes,
        );
        const currentRow =
          normalizedMix[combinationCode] || buildEmptyMixRow(spMethodCodes);
        const requestedValue = roundToTwo(
          Math.max(0, Math.min(100, parseNumberOrZero(rawValue))),
        );
        const otherTotal = spMethodCodes.reduce(
          (sum, code) =>
            code === methodCode ? sum : sum + parseNumberOrZero(currentRow[code]),
          0,
        );
        const allowedValue = roundToTwo(
          Math.min(requestedValue, Math.max(0, 100 - otherTotal)),
        );

        return {
          ...prev,
          combinationMixPercentages: {
            ...normalizedMix,
            [combinationCode]: {
              ...currentRow,
              [methodCode]: allowedValue,
            },
          },
        };
      });
    },
    [combinationCodes, spMethodCodes],
  );

  const handleMethodCostChange = useCallback((methodCode, rawValue) => {
    setBlendedFormData((prev) => ({
      ...prev,
      methodCosts: {
        ...normalizeMethodCosts(prev.methodCosts, spMethodCodes),
        [methodCode]:
          rawValue === "" ? "" : roundToTwo(Math.max(0, parseNumberOrZero(rawValue))),
      },
    }));
  }, [spMethodCodes]);

  const handleBlendedSystemChange = useCallback(
    async (changes) => {
      const nextCurrency = changes.currency ?? blendedFormData.currency;
      const nextUnitOfMeasure =
        changes.unitOfMeasure ?? blendedFormData.unitOfMeasure;
      const currentMethodCosts = normalizeMethodCosts(
        blendedFormData.methodCosts,
        spMethodCodes,
      );

      const systemChanged =
        nextCurrency !== blendedFormData.currency ||
        nextUnitOfMeasure !== blendedFormData.unitOfMeasure;

      if (!systemChanged || spMethodCodes.length === 0) {
        setBlendedFormData((prev) => ({
          ...prev,
          ...changes,
        }));
        return;
      }

      setMethodCostsLoading(true);

      try {
        const convertedMethodCosts = await convertMethodCostsMap({
          methodCosts: currentMethodCosts,
          fromCurrency: blendedFormData.currency,
          toCurrency: nextCurrency,
          fromUnit: blendedFormData.unitOfMeasure,
          toUnit: nextUnitOfMeasure,
          year: blendedFormData.year,
        });

        setBlendedFormData((prev) => ({
          ...prev,
          ...changes,
          methodCosts: convertedMethodCosts,
        }));
      } catch (err) {
        console.error("Error converting blended method costs:", err);
        setBlendedError(
          err.response?.data?.message || "Failed to convert price row values",
        );
      } finally {
        setMethodCostsLoading(false);
      }
    },
    [
      blendedFormData.currency,
      blendedFormData.unitOfMeasure,
      blendedFormData.methodCosts,
      blendedFormData.year,
      convertMethodCostsMap,
      spMethodCodes,
    ],
  );

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMethodModalError(null);

    try {
      if (editingMethod) {
        await api.put(`/surface-prep-methods/${editingMethod.id}`, formData);
      } else {
        await api.post("/surface-prep-methods", formData);
      }

      setMethodModalError(null);
      setShowModal(false);
      setEditingMethod(null);
      setFormData({
        method_code: "",
        method_name: "",
        sort_order: 99,
      });
      fetchMethods();
    } catch (err) {
      setMethodModalError(
        err.response?.data?.message ||
          "Failed to save surface preparation method",
      );
      console.error("Error saving method:", err);
    }
  };

  // Handle edit
  const handleEdit = (method) => {
    setError(null);
    setMethodModalError(null);
    setEditingMethod(method);
    setFormData({
      method_code: method.methodCode,
      method_name: method.methodName,
      sort_order: method.sortOrder ?? method.sort_order ?? 99,
    });
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this surface preparation method?",
      )
    ) {
      try {
        await api.delete(`/surface-prep-methods/${id}`);
        fetchMethods();
      } catch (err) {
        setError("Failed to delete surface preparation method");
        console.error("Error deleting method:", err);
      }
    }
  };

  const handleToggleActive = async (method) => {
    try {
      await api.put(`/surface-prep-methods/${method.id}`, {
        is_active: !getMethodIsActive(method),
      });
      await fetchMethods();
    } catch (err) {
      setError("Failed to update method active status");
      console.error("Error toggling method status:", err);
    }
  };

  // Reset form
  const resetForm = () => {
    setMethodModalError(null);
    setFormData({
      method_code: "",
      method_name: "",
      sort_order: 99,
    });
    setEditingMethod(null);
  };

  // Blended cost handlers
  const handleBlendedFilterChange = (e) => {
    const { name, value } = e.target;
    setBlendedFilters((prev) => ({ ...prev, [name]: value }));
    setBlendedPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const resetBlendedForm = () => {
    setBlendedFormData({
      year: new Date().getFullYear(),
      methodCosts: buildEmptyMethodCosts(spMethodCodes),
      combinationMixPercentages: buildEmptyCombinationMix(
        combinationCodes,
        spMethodCodes,
      ),
      currency: defaultSystemSettings.currency || "CAD",
      unitOfMeasure: defaultSystemSettings.uom,
      isActive: true,
    });
    setEditingYear(null);
  };

  const handleBlendedSubmit = async (e) => {
    e.preventDefault();
    setBlendedLoading(true);
    setBlendedError("");

    try {
      const payload = {
        year: blendedFormData.year,
        methodCosts: normalizeMethodCosts(
          blendedFormData.methodCosts,
          spMethodCodes,
        ),
        combinationMixPercentages: blendedFormData.combinationMixPercentages,
        currency: blendedFormData.currency,
        unitOfMeasure: blendedFormData.unitOfMeasure,
        isActive: blendedFormData.isActive,
      };

      if (editingYear) {
        await api.put(`/blended-surface-prep/year/${editingYear}`, payload);
      } else {
        await api.post("/blended-surface-prep", payload);
      }

      setShowBlendedModal(false);
      resetBlendedForm();
      fetchBlendedCosts();
    } catch (err) {
      setBlendedError(
        err.response?.data?.message || "Failed to save blended costs",
      );
    } finally {
      setBlendedLoading(false);
    }
  };

  const handleBlendedEdit = async (yearData) => {
    try {
      const response = await api.get(
        `/blended-surface-prep/year/${yearData.year}`,
      );
      if (response.data.success) {
        const data = response.data.data;
        setEditingYear(data.year);
        setBlendedFormData({
          year: data.year,
          methodCosts: normalizeMethodCosts(data.methodCosts || {}, spMethodCodes),
          combinationMixPercentages: normalizeCombinationMix(
            data.combinationMixPercentages || data.mixPercentages || {},
            combinationCodes,
            spMethodCodes,
          ),
          currency: data.currency || "CAD",
          unitOfMeasure: data.unitOfMeasure || defaultSystemSettings.uom,
          isActive: data.isActive !== undefined ? data.isActive : true,
        });
        setShowBlendedModal(true);
      }
    } catch (err) {
      setBlendedError("Failed to load blended costs for editing");
      console.error("Error loading blended costs:", err);
    }
  };

  const handleBlendedDelete = async (yearData) => {
    if (window.confirm(`Delete all blended costs for year ${yearData.year}?`)) {
      try {
        await api.delete(`/blended-surface-prep/year/${yearData.year}`);
        fetchBlendedCosts();
      } catch (err) {
        setBlendedError(err.response?.data?.message || "Failed to delete");
      }
    }
  };

  const handleBlendedToggleActive = async (yearData) => {
    try {
      const sourceResponse = await api.get(
        `/blended-surface-prep/year/${yearData.year}`,
      );
      if (!sourceResponse.data?.success || !sourceResponse.data?.data) {
        setBlendedError("Failed to load blended costs for status update");
        return;
      }

      const sourceData = sourceResponse.data.data;

      await api.put(`/blended-surface-prep/year/${yearData.year}`, {
        year: sourceData.year,
        methodCosts: sourceData.methodCosts || {},
        combinationMixPercentages:
          sourceData.combinationMixPercentages || sourceData.mixPercentages || {},
        currency: sourceData.currency || "CAD",
        unitOfMeasure: sourceData.unitOfMeasure || defaultSystemSettings.uom,
        isActive: !yearData.isActive,
      });

      await fetchBlendedCosts();
    } catch (err) {
      setBlendedError(
        err.response?.data?.message || "Failed to update blended status",
      );
    }
  };

  const handleBlendedCopyFromYear = (sourceYear) => {
    const usedYears = new Set(
      (blendedCosts || [])
        .map((row) => Number(row.year))
        .filter((year) => Number.isFinite(year)),
    );

    let suggestedYear = Number(sourceYear) + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setBlendedCopySourceYear(Number(sourceYear));
    setBlendedCopyTargetYear(String(suggestedYear));
    setBlendedCopyError("");
    setShowBlendedCopyModal(true);
  };

  const handleConfirmBlendedCopy = async () => {
    if (!blendedCopySourceYear) return;

    const targetYear = parseInt(blendedCopyTargetYear, 10);
    if (
      !Number.isInteger(targetYear) ||
      targetYear < 1900 ||
      targetYear > 3000
    ) {
      setBlendedCopyError("Please enter a valid target year.");
      return;
    }
    if (targetYear === blendedCopySourceYear) {
      setBlendedCopyError("Target year must be different from source year.");
      return;
    }

    try {
      setBlendedCopying(true);
      setBlendedCopyError("");

      const sourceResponse = await api.get(
        `/blended-surface-prep/year/${blendedCopySourceYear}`,
      );
      if (!sourceResponse.data?.success || !sourceResponse.data?.data) {
        setBlendedCopyError("Failed to load source year blended data.");
        return;
      }

      const sourceData = sourceResponse.data.data;

      await api.post("/blended-surface-prep", {
        year: targetYear,
        methodCosts: sourceData.methodCosts || {},
        combinationMixPercentages:
          sourceData.combinationMixPercentages || sourceData.mixPercentages || {},
        currency: sourceData.currency || "CAD",
        unitOfMeasure: sourceData.unitOfMeasure || defaultSystemSettings.uom,
        isActive:
          sourceData.isActive !== undefined ? sourceData.isActive : true,
      });

      setShowBlendedCopyModal(false);
      setBlendedCopySourceYear(null);
      setBlendedCopyTargetYear("");
      await fetchBlendedCosts();
    } catch (err) {
      setBlendedCopyError(
        err.response?.data?.message || "Failed to copy blended costs",
      );
    } finally {
      setBlendedCopying(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start gap-2">
        <p className="text-gray-600">
          Manage surface preparation methods and blended costs
        </p>
        <InlineFieldTooltip
          page="surface_prep_methods"
          section="page_description"
          fieldKey="surface_prep_methods_overview"
        />
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("methods")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "methods"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Methods
          </button>
          <button
            onClick={() => setActiveTab("blended")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "blended"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Blended Costs
          </button>
        </nav>
      </div>

      {/* Error Message */}
      {(error || blendedError) && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {activeTab === "methods" ? error : blendedError}
        </div>
      )}

      {/* Methods Tab Content */}
      {activeTab === "methods" && (
        <>
          {/* Filters and Search */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search methods..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => {
                setError(null);
                setMethodModalError(null);
                resetForm();
                setShowModal(true);
              }}
              className="px-4 py-2 rounded-md focus:outline-none focus:ring-2 flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
              title="Add a surface prep method"
            >
              <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
              Add Method
            </button>
          </div>

          {/* Methods Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {loading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading methods...</p>
              </div>
            ) : methods.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <p>No surface preparation methods found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Method Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Method Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sort Order
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortMethodsBySortOrder(methods).map((method) => (
                      <tr key={method.id} className="hover:bg-gray-50 bg-white">
                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit bg-blue-100 text-blue-800">
                            {method.methodCode || method.method_code}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-700">
                          {method.methodName || method.method_name}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                          {Number(method.sortOrder ?? method.sort_order ?? 99).toFixed(2)}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={getMethodIsActive(method)}
                            onClick={() => handleToggleActive(method)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${getMethodIsActive(method) ? "bg-blue-600" : "bg-gray-300"}`}
                            title="Toggle active status"
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${getMethodIsActive(method) ? "translate-x-6" : "translate-x-1"}`}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(method)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit method"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>
                            <button
                              onClick={() => handleDelete(method.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete method"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination for Methods list */}
          {pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() =>
                    setPagination({ ...pagination, page: pagination.page - 1 })
                  }
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setPagination({ ...pagination, page: pagination.page + 1 })
                  }
                  disabled={pagination.page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total,
                      )}
                    </span>{" "}
                    of <span className="font-medium">{pagination.total}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() =>
                        setPagination({
                          ...pagination,
                          page: pagination.page - 1,
                        })
                      }
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {Array.from(
                      { length: pagination.pages },
                      (_, i) => i + 1,
                    ).map((page) => (
                      <button
                        key={page}
                        onClick={() => setPagination({ ...pagination, page })}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() =>
                        setPagination({
                          ...pagination,
                          page: pagination.page + 1,
                        })
                      }
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-50 px-4 py-24">
          <div className="relative mx-auto w-full max-w-3xl rounded-md border bg-white p-5 shadow-lg">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>
                  {editingMethod
                    ? "Edit Surface Preparation Method"
                    : "Add New Surface Preparation Method"}
                </span>
                <InlineFieldTooltip
                  page="surface_prep_methods"
                  section="modal_titles"
                  fieldKey="surface_prep_method_form"
                  fallbackText="Create or edit a surface preparation method."
                  title="Surface Preparation Method Form"
                />
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                This form manages the surface prep method list. Cost fields were removed from Methods and are managed in the Blended Costs tab.
              </p>
              {methodModalError && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {methodModalError}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Method Code *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.method_code}
                      onChange={(e) =>
                        {
                          setMethodModalError(null);
                          setFormData({
                            ...formData,
                            method_code: e.target.value.toUpperCase(),
                          });
                        }
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., SP-12"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Method codes must be unique.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Method Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.method_name}
                      onChange={(e) =>
                        {
                          setMethodModalError(null);
                          setFormData({
                            ...formData,
                            method_name: e.target.value,
                          });
                        }
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., SP-2 Surface Preparation"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort Order *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.sort_order}
                      onChange={(e) =>
                        {
                          setMethodModalError(null);
                          setFormData({
                            ...formData,
                            sort_order:
                              e.target.value === ""
                                ? ""
                                : parseFloat(e.target.value),
                          });
                        }
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 1.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lower numbers appear first.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingMethod ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Blended Costs Tab Content */}
      {activeTab === "blended" && (
        <>
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year
                </label>
                <input
                  type="number"
                  name="year"
                  value={blendedFilters.year}
                  onChange={handleBlendedFilterChange}
                  placeholder="Filter by year..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Combination
                </label>
                <select
                  name="combination_code"
                  value={blendedFilters.combination_code}
                  onChange={handleBlendedFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Combinations</option>
                  {combinations.map((combo) => (
                    <option key={combo.id} value={combo.code}>
                      {combo.code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="is_active"
                  value={blendedFilters.is_active}
                  onChange={handleBlendedFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setBlendedFilters({
                      year: "",
                      combination_code: "",
                      is_active: "",
                    });
                    setBlendedPagination((prev) => ({
                      ...prev,
                      currentPage: 1,
                    }));
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Add Button */}
          <div className="mb-4">
            <button
              onClick={() => {
                resetBlendedForm();
                setShowBlendedModal(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Blended Costs (Year)
            </button>
          </div>

          {/* Table - One row per year with 9 combination columns */}
          <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
            {blendedLoading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                        Year
                      </th>
                      {combinationCodes.map((code) => (
                        <th
                          key={code}
                          className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase"
                        >
                          {code}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Used In Costing
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {blendedCosts.map((yearData) => (
                      <tr key={yearData.year} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                          {yearData.year}
                        </td>
                        {combinationCodes.map((code) => (
                          <td
                            key={code}
                            className="px-3 py-4 whitespace-nowrap text-center"
                          >
                            <div className="text-sm font-medium text-gray-900">
                              {yearData.costs && yearData.costs[code] != null
                                ? formatRateValue(yearData.costs[code])
                                : "0.00"}
                            </div>
                          </td>
                        ))}
                        <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                          CAD/sqm
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={!!yearData.isActive}
                            onClick={() => handleBlendedToggleActive(yearData)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              yearData.isActive ? "bg-blue-600" : "bg-gray-300"
                            }`}
                            title="Toggle active status"
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                yearData.isActive
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() =>
                              handleBlendedCopyFromYear(yearData.year)
                            }
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 mr-3"
                            title="Copy to new year"
                          >
                            Copy
                          </button>
                          <button
                            onClick={() => handleBlendedEdit(yearData)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                          <button
                            onClick={() => handleBlendedDelete(yearData)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {blendedPagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {blendedPagination.currentPage} of{" "}
                {blendedPagination.totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() =>
                    setBlendedPagination((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage - 1,
                    }))
                  }
                  disabled={!blendedPagination.hasPrevPage}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setBlendedPagination((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage + 1,
                    }))
                  }
                  disabled={!blendedPagination.hasNextPage}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Blended Costs Modal */}
          {showBlendedModal && (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-50 px-4 py-24">
              <div className="relative mx-auto w-[96%] max-w-7xl rounded-md border bg-white p-5 shadow-lg">
                <div className="mt-3">
                  <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                    <span>
                      {editingYear
                        ? `Edit Blended Costs for Year ${editingYear}`
                        : "Add New Blended Costs (Year)"}
                    </span>
                    <InlineFieldTooltip
                      page="surface_prep_methods"
                      section="modal_titles"
                      fieldKey="blended_costs_form"
                      fallbackText="Create or edit blended surface preparation costs by year."
                      title="Blended Costs Form"
                    />
                  </h3>

                  {blendedError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-800 text-sm">{blendedError}</p>
                    </div>
                  )}

                  <form onSubmit={handleBlendedSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Year *
                        </label>
                        <input
                          type="number"
                          value={blendedFormData.year}
                          onChange={(e) => {
                            const newYear = parseInt(e.target.value);
                            setBlendedFormData({
                              ...blendedFormData,
                              year: newYear,
                            });
                          }}
                          required
                          disabled={!!editingYear}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Currency *
                        </label>
                        <select
                          value={blendedFormData.currency}
                          onChange={(e) =>
                            handleBlendedSystemChange({
                              currency: e.target.value,
                            })
                          }
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="CAD">CAD</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Unit of Measure *
                        </label>
                        <select
                          value={blendedFormData.unitOfMeasure}
                          onChange={(e) =>
                            handleBlendedSystemChange({
                              unitOfMeasure: e.target.value,
                            })
                          }
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="sqft">sqft</option>
                          <option value="sqm">sqm</option>
                        </select>
                      </div>
                    </div>

                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                      Enter yearly method prices in the Price row. Combination rows must total 100%. The `CAD/sqm` column is the canonical normalized value used in simulation and repair-project costing.
                    </div>

                    {combinationCodes.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No corrosion and damage combinations are configured yet.
                      </p>
                    ) : spMethodCodes.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No surface prep methods are available yet. Add methods in the Methods tab first.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
                          <table className="min-w-full border-collapse text-xs">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="sticky left-0 z-20 min-w-[78px] border border-slate-200 bg-slate-100 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                                  Combo
                                </th>
                                {sortedSurfacePrepMethods.map((method) => {
                                  const methodCode = method.methodCode || method.method_code;
                                  return (
                                    <th
                                      key={methodCode}
                                      className="min-w-[76px] border border-slate-200 px-1 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-600"
                                    >
                                      {!getMethodIsActive(method) && (
                                        <div className="text-[9px] font-semibold uppercase text-amber-600">off</div>
                                      )}
                                      <div>{methodCode}</div>
                                    </th>
                                  );
                                })}
                                <th className="min-w-[86px] border border-slate-200 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                                  {blendedFormData.currency}/{blendedFormData.unitOfMeasure}
                                </th>
                                <th className="min-w-[86px] border border-slate-200 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                                  CAD/sqm
                                </th>
                                <th className="min-w-[72px] border border-slate-200 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                                  100%
                                </th>
                              </tr>
                              <tr className="bg-amber-50">
                                <th className="sticky left-0 z-10 border border-slate-200 bg-amber-50 px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                                  Price
                                </th>
                                {sortedSurfacePrepMethods.map((method) => {
                                  const methodCode = method.methodCode || method.method_code;
                                  return (
                                    <th
                                      key={`${methodCode}-price`}
                                      className="border border-slate-200 p-0.5"
                                    >
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={
                                          parseNumberOrZero(
                                            blendedFormData.methodCosts?.[methodCode],
                                          ) === 0
                                            ? ""
                                            : blendedFormData.methodCosts?.[methodCode]
                                        }
                                        onChange={(e) =>
                                          handleMethodCostChange(methodCode, e.target.value)
                                        }
                                        disabled={methodCostsLoading}
                                        className="h-8 w-full border-0 bg-amber-50 px-1 text-center text-[11px] font-semibold text-amber-900 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
                                      />
                                    </th>
                                  );
                                })}
                                <th className="border border-slate-200 px-2 py-1.5 text-center text-[11px] font-medium text-amber-800">
                                  auto
                                </th>
                                <th className="border border-slate-200 px-2 py-1.5 text-center text-[11px] font-medium text-amber-800">
                                  used
                                </th>
                                <th className="border border-slate-200 px-2 py-1.5 text-center text-[11px] font-medium text-amber-800">
                                  req
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {combinationCodes.map((combinationCode) => {
                                const rowMetrics =
                                  blendedRowMetrics[combinationCode] || {};

                                return (
                                  <tr key={combinationCode}>
                                    <td className="sticky left-0 z-10 border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-900">
                                      {combinationCode}
                                    </td>
                                    {sortedSurfacePrepMethods.map((method) => {
                                      const methodCode = method.methodCode || method.method_code;
                                      const currentValue =
                                        blendedFormData.combinationMixPercentages?.[
                                          combinationCode
                                        ]?.[methodCode] ?? 0;

                                      return (
                                        <td key={`${combinationCode}-${methodCode}`} className="border border-slate-200 p-0.5">
                                          <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={currentValue === 0 ? "" : currentValue}
                                            onChange={(e) =>
                                              handleBlendedPercentageChange(
                                                combinationCode,
                                                methodCode,
                                                e.target.value,
                                              )
                                            }
                                            className="h-8 w-full border-0 bg-transparent px-1 text-center text-xs focus:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          />
                                        </td>
                                      );
                                    })}
                                    <td className="border border-slate-200 px-2 py-1.5 text-center text-[11px] font-semibold text-slate-900">
                                      {formatRateValue(rowMetrics.totalCost)}
                                    </td>
                                    <td className="border border-slate-200 px-2 py-1.5 text-center text-[11px] font-semibold text-slate-900">
                                      {formatRateValue(rowMetrics.normalizedCadPerSqmTotalCost)}
                                    </td>
                                    <td
                                      className={`border border-slate-200 px-2 py-1.5 text-center text-[11px] font-semibold ${
                                        rowMetrics.isValid
                                          ? "text-emerald-700"
                                          : "bg-rose-50 text-rose-700"
                                      }`}
                                    >
                                      {formatRateValue(rowMetrics.percentageTotal)}%
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {invalidCombinationCodes.length > 0 && (
                          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            Save is disabled until every combination row totals exactly 100%.
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={blendedFormData.isActive}
                        onChange={(e) =>
                          setBlendedFormData({
                            ...blendedFormData,
                            isActive: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        Active
                      </label>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => {
                          setShowBlendedModal(false);
                          resetBlendedForm();
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={blendedLoading || !canSaveBlendedForm}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {blendedLoading
                          ? "Saving..."
                          : editingYear
                            ? "Update"
                            : "Create"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {showBlendedCopyModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
                <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-2">
                  <span>Copy To Year</span>
                  <InlineFieldTooltip
                    page="surface_prep_methods"
                    section="modal_titles"
                    fieldKey="copy_blended_costs_to_year"
                    fallbackText="Copy blended costs from one year to another."
                    title="Copy To Year"
                  />
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Copy blended costs from{" "}
                  <strong>{blendedCopySourceYear}</strong> to a new year.
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Year
                </label>
                <input
                  type="number"
                  value={blendedCopyTargetYear}
                  onChange={(e) => {
                    setBlendedCopyTargetYear(e.target.value);
                    setBlendedCopyError("");
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 2027"
                />
                {blendedCopyError && (
                  <p className="mt-2 text-sm text-red-600">
                    {blendedCopyError}
                  </p>
                )}
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (blendedCopying) return;
                      setShowBlendedCopyModal(false);
                      setBlendedCopySourceYear(null);
                      setBlendedCopyTargetYear("");
                      setBlendedCopyError("");
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={blendedCopying}
                    onClick={handleConfirmBlendedCopy}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {blendedCopying ? "Copying..." : "Copy"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SurfacePrepMethodsManagement;
